import { useEffect, useRef, useState } from "react";
import { Send, Paperclip, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fileUrl, fmtDate } from "@/lib/api";

export function ChatBox({ messages, onSend, meId, testId = "chat", placeholder = "Escribe un mensaje…", priv = true, renderExtra }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const endRef = useRef();
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);
  const send = async (image_url = null) => {
    if (!text.trim() && !image_url) return;
    setBusy(true);
    try { await onSend(text.trim(), image_url); setText(""); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const attach = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/upload?private=${priv}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onSend(text.trim(), data.path); setText("");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="neon-card p-3 flex flex-col" data-testid={testId}>
      <div className="max-h-64 min-h-[80px] overflow-auto space-y-2 pr-1" data-testid={`${testId}-messages`}>
        {messages.length === 0 && <div className="text-xs text-muted-foreground">Sin mensajes todavía.</div>}
        {messages.map((m) => {
          const mine = (m.user_id || m.sender_id) === meId;
          const sys = m.kind === "system" || m.kind === "quote";
          return (
            <div key={m.id} className={`text-sm max-w-[85%] rounded-xl px-3 py-2 ${sys ? "mx-auto bg-amber-500/15 text-amber-200 text-center" : mine ? "ml-auto bg-primary/20" : "bg-secondary"}`}>
              {!sys && <div className="text-[10px] text-muted-foreground">{m.user_name || m.sender_name} · {fmtDate(m.created_at)}</div>}
              {m.image_url && <a href={fileUrl(m.image_url, priv)} target="_blank" rel="noreferrer"><img src={fileUrl(m.image_url, priv)} alt="" className="rounded-lg max-h-40 mb-1" /></a>}
              {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
              {renderExtra && renderExtra(m)}
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 mt-2">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={attach} />
        <Button type="button" variant="outline" size="icon" className="h-11 w-11 shrink-0" data-testid={`${testId}-attach`} onClick={() => fileRef.current?.click()} disabled={busy}><Paperclip size={16} /></Button>
        <Input data-testid={`${testId}-input`} className="h-11" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={placeholder} />
        <Button data-testid={`${testId}-send`} onClick={() => send()} disabled={busy} className="h-11 shrink-0">{busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}</Button>
      </div>
    </div>
  );
}

export function Stars({ value = 0, onChange, size = 18, testId }) {
  return (
    <div className="flex gap-0.5" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} data-testid={testId ? `${testId}-${n}` : undefined} className={onChange ? "cursor-pointer" : "cursor-default"}>
          <Star size={size} className={n <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-muted-foreground"} />
        </button>
      ))}
    </div>
  );
}
